import { Alert, Anchor, Button, PasswordInput, Stack, Text, TextInput } from '@mantine/core'
import { useForm } from '@mantine/form'
import { useQueryClient } from '@tanstack/react-query'
import { appOfPath } from '@tnn/core'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'
import { authClient, meQueryKey } from '../auth/session'
import { AuthCard } from './AuthCard'
import { appLink, callbackTarget } from './links'
import { useNavigate } from './scope'

/**
 * Why signing in failed: an account still waiting for approval or closed says so, a name locked by wrong passwords
 * (`ACCOUNT_LOCKED`, with its minutes) or too many tries from here (429); anything else is the usual message
 */
const failures: Record<string, string> = {
  ACCOUNT_PENDING: 'auth.pending',
  ACCOUNT_DISABLED: 'auth.disabled',
  ACCOUNT_LOCKED: 'auth.locked',
}

type AuthFailure = { code?: string; status?: number; minutes?: number }

/**
 * Signing in for an assistant (ChatGPT, Claude: `/login` with an OAuth query): Better Auth answers with where the flow
 * goes on, its page that asks to allow the assistant or the assistant itself
 */
const oauthNext = (data: unknown) => {
  const url = (data as { url?: unknown } | null)?.url
  return typeof url === 'string' ? url : null
}

/**
 * The one sign-in page, Thananan OS's `/login`, for every app: afterwards it goes to `callbackUrl` (or the older
 * `redirect`), a page of any app on this site (`callbackTarget`), or home — where the apps ask for a PIN the first time
 * (auth/Pin.tsx); the password opens the session it makes
 */
export function LoginPage({ appName }: { appName: string }) {
  const { t } = useTranslation()
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const client = useQueryClient()
  const [failed, setFailed] = useState<{ key: string; minutes?: number } | null>(null)
  const form = useForm({ initialValues: { username: '', password: '' } })

  const signedIn = async (data: unknown) => {
    const next = oauthNext(data)
    if (next) return window.location.assign(next)
    await client.invalidateQueries({ queryKey: meQueryKey })
    const target = callbackTarget(params.get('callbackUrl') ?? params.get('redirect'))
    // A page of Thananan OS opens in place; another app's loads that app
    if (appOfPath(target) === 'portal') navigate(target, { replace: true })
    else window.location.replace(target)
  }

  const submit = form.onSubmit(async ({ username, password }) => {
    setFailed(null)
    const { data, error } = await authClient.signIn.username({ username: username.trim().toLowerCase(), password })
    if (error) {
      const { code, status, minutes } = error as AuthFailure
      setFailed({ key: failures[code ?? ''] ?? (status === 429 ? 'auth.tooMany' : 'auth.invalid'), minutes })
      return
    }
    await signedIn(data)
  })

  return (
    <AuthCard title={t('auth.signInTitle')} subtitle={`${appName} · ${t('auth.signInSubtitle')}`} onSubmit={submit}>
      <Stack gap="md">
        {failed && (
          <Alert color={failed.key === 'auth.pending' ? 'warning' : 'danger'} variant="light" radius="md">
            {t(failed.key, { minutes: failed.minutes })}
          </Alert>
        )}
        <TextInput
          size="md"
          label={t('auth.username')}
          autoComplete="username"
          autoCapitalize="none"
          autoFocus
          required
          {...form.getInputProps('username')}
        />
        <PasswordInput size="md" label={t('auth.password')} autoComplete="current-password" required {...form.getInputProps('password')} />
        <Button type="submit" size="md" mt="sm" loading={form.submitting} fullWidth>
          {t('auth.signIn')}
        </Button>
        <Text size="sm" c="dimmed" ta="center">
          {t('auth.forgot')}
        </Text>
        <Text size="sm" ta="center">
          {t('auth.noAccount')} <Anchor href={appLink('portal', '/signup')}>{t('auth.signUp')}</Anchor>
        </Text>
      </Stack>
    </AuthCard>
  )
}
